import React, {
	memo,
	useCallback
} from 'react';
import PropTypes from 'prop-types';

import FormCheckbox from '../Form/FormCheckbox';
import FormCheckboxGroup from '../Form/FormCheckboxGroup';
import FormDatePicker from '../Form/FormDatePicker';
import FormFileInput from '../Form/FormFileInput';
import FormInput from '../Form/FormInput';
import FormRadio from '../Form/FormRadio';
import FormSelect from '../Form/FormSelect';
import FormTextArea from '../Form/FormTextArea';
import FormTimeInput from '../Form/FormTimeInput';
import { Divider } from 'semantic-ui-react';

import styles from './document-modal.module.css';

const DynamicFormField = ( {
	data,
	errors,
	field,
	forceValidation,
	updateField
} ) => {
	const {
		config,
		id,
		description,
		formatter,
		label,
		layout: {
			align,
			width
		},
		mask,
		options,
		type,
		validation: {
			max,
			maxLength,
			min,
			maxWords,
			required
		}
	} = field;

	const renderField = useCallback( () => {
		switch ( type ) {
			case 1: { // paragraph
				return (
					<FormTextArea
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						maxLength={ maxLength }
						maxWords={ maxWords }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						rows={ 6 }
						useTooltip
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 2: { // numeric
				const { allowFloat } = config;

				return (
					<FormInput
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						formatter={ formatter }
						instructions={ description }
						key={ id }
						keyDown={ allowFloat
							? 'float'
							: 'integer'
						}
						mask={ mask }
						max={ max }
						min={ min }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						type="number"
						value={ data ?? '' }
						width={ width }
					/>
				);
			}

			case 3: { // header
				return <div className={ styles.dynamicFormHeader }>
					<Divider hidden />
					{ label && <h4>{ label }</h4> }
					<p>{ description }</p>
					<Divider/>
				</div>;
			}

			case 4: { // group numeric
				const { allowFloat } = config;

				return (
					<FormInput
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						fieldLabelAlignment={ 'center' }
						fieldLabelPosition={ 'below' }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						formatter={ formatter }
						instructions={ description }
						key={ id }
						keyDown={ allowFloat
							? 'float'
							: 'integer'
						}
						mask={ mask }
						max={ max }
						min={ min }
						name={ `${ id }` }
						onChange={ updateField }
						onClick={ e => e.target.select() }
						type="number"
						value={ data ?? '' }
						width={ 3 }
					/>
				);
			}

			case 5: { // text
				return (
					<FormInput
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						formatter={ formatter }
						instructions={ description }
						key={ id }
						mask={ mask }
						maxLength={ maxLength }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 6: { // dropdown
				const { search, optionGroup } = config;

				const fieldOptions = options.map( ( { id, label } ) => ( {
					key: id,
					value: id,
					text: label
				} ) );

				return (
					<FormSelect
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						maxLength={ maxLength }
						name={ `${ id }` }
						onChange={ updateField }
						options={ fieldOptions }
						optionGroups={ optionGroup }
						required={ required }
						rows={ 6 }
						search={ search }
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 7: { // datepicker
				return (
					<FormDatePicker
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 8: { // checkbox
				return (
					<FormCheckbox
						align={ align }
						checked={ data }
						fieldLabel={ label }
						instructions={ description }
						key={ id }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						width={ width }
					/>
				);
			}

			case 9: { // group checkbox
				const fieldOptions = options.map( ( { id, label } ) => ( {
					key: id,
					value: id,
					text: label
				} ) );

				return (
					<FormCheckboxGroup
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						name={ `${ id }` }
						onChange={ updateField }
						options={ fieldOptions }
						required={ required }
						value={ data || [] }
						width={ width }
					/>
				);
			}

			case 10: { // radio
				// const {
				// 	inline
				// } = config;

				const fieldOptions = options.map( ( { id, label } ) => ( {
					key: id,
					value: id,
					text: label
				} ) );

				return (
					<FormRadio
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						name={ `${ id }` }
						onChange={ updateField }
						options={ fieldOptions }
						required={ required }
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 11: { // file
				const {
					download,
					hasUploadComponent,
					supportsMultiple
				} = config;

				const value = download
					? download.title
					: data
						? data.filename
						: null;

				return (
					<FormFileInput
						align={ align }
						allowMultiple={ supportsMultiple }
						download={ download || data }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						icon={ hasUploadComponent }
						key={ id }
						name={ `${ id }` }
						onUpload={ hasUploadComponent
							? updateField
							: null
						}
						required={ required }
						upload={ hasUploadComponent }
						value={ value }
						width={ width }
					/>
				);
			}

			case 12: { // timepicker
				return (
					<FormTimeInput
						align={ align }
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						maxLength={ 5 }
						name={ `${ id }` }
						onChange={ updateField }
						required={ required }
						value={ data || '' }
						width={ width }
					/>
				);
			}

			case 13: { // multi dropdown
				const { optionGroup } = config;

				const fieldOptions = options.map( ( { id, label } ) => ( {
					key: id,
					value: id,
					text: label
				} ) );

				return (
					<FormSelect
						align={ align }
						clearable
						errors={ errors }
						fieldLabel={ label }
						forceValidation={ errors.length
							? forceValidation
							: false
						}
						instructions={ description }
						key={ id }
						maxLength={ maxLength }
						multiple
						name={ `${ id }` }
						onChange={ updateField }
						options={ fieldOptions }
						optionGroups={ optionGroup }
						required={ required }
						rows={ 6 }
						search
						value={ data || [] }
						width={ width }
					/>
				);
			}

			default:
				return null;
		}
	}, [
		type,
		align,
		errors,
		label,
		forceValidation,
		description,
		id,
		maxLength,
		maxWords,
		updateField,
		required,
		data,
		width,
		config,
		formatter,
		mask,
		max,
		min,
		options
	] );

	return (
		<>
			{ renderField() }
		</>
	);
};

DynamicFormField.propTypes = {
	data: PropTypes.any,
	errors: PropTypes.array,
	field: PropTypes.object.isRequired,
	forceValidation: PropTypes.bool,
	updateField: PropTypes.func.isRequired
};

export default memo( DynamicFormField );
